// src/Pages/GeneratorPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Alert,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import shieldLogo from '../assets/RHP Shield Logo.svg';
import StaffOwnerSelect from '../Components/StaffOwnerSelect';
import { useUser } from '../Components/useUser';
import { supabase } from '../utils/supabaseClient';
import {
  buildStaffAttributionFields,
  STAFF_AUTH_REQUIRED_MESSAGE,
} from '../utils/attribution';

import TargetUrlInput from '../Components/TargetUrlInput';
import UtmFields from '../Components/UtmFields';
import PdfModeFields from '../Components/PdfModeFields';
import SaveOptions from '../Components/SaveOptions';
import ColorPicker from '../Components/Generator/ColorPicker';
import QrStyleControls from '../Components/Generator/QrStyleControls';
import LogoUpload from '../Components/LogoUpload';
import ExportButtons from '../Components/ExportButtons';
import QrCanvas from '../Components/QrCanvas';
import ToastAlerts from '../Components/ToastAlerts';

import generateShortCode from '../utils/generateShortCode';
import sanitizeShortcode, { withRandomSuffix } from '../utils/sanitizeShortcode';
import sanitizeFilename from '../utils/sanitizeFilename';
import {
  DEFAULT_QR_LOGO_BUCKET,
  resolveQrLogoMetadata,
} from '../utils/resolveQrLogoMetadata';
import {
  clampQrLogoScale,
  DEFAULT_QR_PRESET,
  getQrPresetConfig,
  isQrContrastLow,
} from '../utils/qrStyleOptions';
import usePersistentFormDraft from '../utils/usePersistentFormDraft';
import { buildAppUrl, getAppOrigin } from '../utils/appOrigin';

const PREVIEW_ONLY_PATH = '/preview-only';
const APP_ORIGIN_REQUIRED_MESSAGE = 'Unable to determine this application\'s address. Reload the page and try again.';
const PDF_BUCKET = 'marketing-pdfs';
const QR_LOGO_BUCKET = process.env.REACT_APP_QR_LOGO_BUCKET || DEFAULT_QR_LOGO_BUCKET;
const INTERNAL_APP_TITLE = 'QR Code Generator | Road Home';
const roadHomeSoftPreset = getQrPresetConfig(DEFAULT_QR_PRESET);
const GENERATOR_FORM_DRAFT_KEY = 'generator_page';
const GENERATOR_FORM_DRAFT_VERSION = 'v1';
const DEFAULT_RHP_LOGO_SCAN_SAFE_SCALE = 0.3;

// Safe URL validator
const isValidUrl = (value) => {
  try {
    // Require protocol for clarity
    const u = new URL(value);
    return !!u.protocol && !!u.host;
  } catch {
    return false;
  }
};

const normalizeTrackingValue = (value) => String(value || '')
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-+|-+$/g, '');

const buildTrackedUrl = ({ baseUrl, utmSource, utmMedium, utmCampaign }) => {
  const url = new URL(baseUrl);
  const params = new URLSearchParams(url.search);

  if (utmSource) {
    params.set('utm_source', utmSource);
  } else {
    params.delete('utm_source');
  }

  if (utmMedium) {
    params.set('utm_medium', utmMedium);
  } else {
    params.delete('utm_medium');
  }

  if (utmCampaign) {
    params.set('utm_campaign', utmCampaign);
  } else {
    params.delete('utm_campaign');
  }

  url.search = params.toString();
  return url.toString();
};

const GeneratorPage = () => {
  const auth = useUser() || {};
  const user = auth.user || null;
  const profile = auth.profile || null;
  const [mode, setMode] = useState('url');

  // URL + UTM state
  const [baseUrl, setBaseUrl] = useState('https://roadhomeprogram.org/');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [fileTitle, setFileTitle] = useState('');

  // QR visuals
  const [foregroundColor, setForegroundColor] = useState(roadHomeSoftPreset.foregroundColor);
  const [backgroundColor, setBackgroundColor] = useState(roadHomeSoftPreset.backgroundColor);
  const [logoFile, setLogoFile] = useState(null);
  const [logoScale, setLogoScale] = useState(0.2);
  const [qrPreset, setQrPreset] = useState(DEFAULT_QR_PRESET);
  const [dotStyle, setDotStyle] = useState(roadHomeSoftPreset.dotStyle);
  const [cornerSquareStyle, setCornerSquareStyle] = useState(roadHomeSoftPreset.cornerSquareStyle);
  const [cornerDotStyle, setCornerDotStyle] = useState(roadHomeSoftPreset.cornerDotStyle);

  // Save options + feedback
  const [linkType, setLinkType] = useState('qr');
  const [isSaving, setIsSaving] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Error saving QR code. Please try again.');

  // Results
  const [savedResult, setSavedResult] = useState(null);
  const [hasUnsavedPayloadChanges, setHasUnsavedPayloadChanges] = useState(false);

  // Shortcode controls
  const [customShort, setCustomShort] = useState('');
  const [useCampaignAsShort, setUseCampaignAsShort] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [draftAutosaveEnabled, setDraftAutosaveEnabled] = useState(false);
  const [draftRestoreNotice, setDraftRestoreNotice] = useState('');

  const canvasRef = useRef(null);
  const userId = user?.id || profile?.id || '';
  const isBaseUrlValid = isValidUrl(baseUrl);
  const showBaseUrlError = Boolean(baseUrl && !isBaseUrlValid);
  const normalizedUtmSource = normalizeTrackingValue(utmSource);
  const normalizedUtmMedium = normalizeTrackingValue(utmMedium);
  const normalizedUtmCampaign = normalizeTrackingValue(utmCampaign);
  const trackedDestinationUrl = mode === 'url' && isBaseUrlValid
    ? buildTrackedUrl({
      baseUrl,
      utmSource: normalizedUtmSource,
      utmMedium: normalizedUtmMedium,
      utmCampaign: normalizedUtmCampaign,
    })
    : '';
  const isLinkOnly = linkType === 'link';
  // Short links belong to whichever OutreachOS environment is serving the page.
  const unsavedPreviewUrl = buildAppUrl(PREVIEW_ONLY_PATH) || PREVIEW_ONLY_PATH;
  const unsavedPreviewTarget = mode === 'url' ? trackedDestinationUrl : unsavedPreviewUrl;
  const effectiveLogoScale = logoFile
    ? clampQrLogoScale(logoScale)
    : DEFAULT_RHP_LOGO_SCAN_SAFE_SCALE;
  const showContrastWarning = isQrContrastLow(foregroundColor, backgroundColor);
  const disableSave = mode === 'url'
    ? !isBaseUrlValid || !normalizedUtmSource || !normalizedUtmMedium || !normalizedUtmCampaign
    : !(fileTitle.trim() && pdfFile);
  const generatorDraftValues = useMemo(() => ({
    mode,
    baseUrl,
    utmSource,
    utmMedium,
    utmCampaign,
    fileTitle,
    foregroundColor,
    backgroundColor,
    logoScale,
    qrPreset,
    dotStyle,
    cornerSquareStyle,
    cornerDotStyle,
    linkType,
    customShort,
    useCampaignAsShort,
    selectedOwnerId,
  }), [
    backgroundColor,
    baseUrl,
    cornerDotStyle,
    cornerSquareStyle,
    customShort,
    dotStyle,
    fileTitle,
    foregroundColor,
    linkType,
    logoScale,
    mode,
    qrPreset,
    selectedOwnerId,
    useCampaignAsShort,
    utmCampaign,
    utmMedium,
    utmSource,
  ]);
  const formSignature = JSON.stringify({
    ...generatorDraftValues,
    pdfFile: pdfFile ? [pdfFile.name, pdfFile.size, pdfFile.lastModified] : null,
    logoFile: logoFile ? [logoFile.name, logoFile.size, logoFile.lastModified] : null,
  });
  const hasSavedQr = savedResult?.formSignature === formSignature
    && savedResult?.pdfFile === pdfFile
    && savedResult?.logoFile === logoFile;
  const shortCode = hasSavedQr ? savedResult.shortCode : '';
  const shortUrl = hasSavedQr ? savedResult.shortUrl : '';
  const previewTarget = shortUrl || unsavedPreviewTarget;
  const previewStatusCopy = hasSavedQr
    ? 'QR Saved. Tracking active.'
    : hasUnsavedPayloadChanges
      ? 'Preview only. Changes not saved yet.'
      : 'Preview only. Generate QR to create a tracked Road Home short link before copying or downloading.';
  useEffect(() => {
    if (savedResult && !hasSavedQr) {
      setSavedResult(null);
      setHasUnsavedPayloadChanges(true);
    }
  }, [hasSavedQr, savedResult]);
  const {
    restoreCandidate,
    continueDraft,
    clearDraft,
    draftStatusText,
    draftStatusTone,
  } = usePersistentFormDraft({
    enabled: Boolean(userId),
    userId,
    formKey: GENERATOR_FORM_DRAFT_KEY,
    entityId: null,
    formVersion: GENERATOR_FORM_DRAFT_VERSION,
    formValues: generatorDraftValues,
    source: GENERATOR_FORM_DRAFT_KEY,
    autosaveEnabled: draftAutosaveEnabled,
    restoreFormValues: (draftFormValues) => ({
      ...generatorDraftValues,
      ...(draftFormValues && typeof draftFormValues === 'object' ? draftFormValues : {}),
    }),
  });

  useEffect(() => {
    document.title = INTERNAL_APP_TITLE;
  }, []);

  useEffect(() => {
    let active = true;

    const restoreDraft = async () => {
      if (!restoreCandidate) return;

      const restoredDraft = await continueDraft();
      if (!active || !restoredDraft?.formValues) return;

      const draftValues = restoredDraft.formValues;
      const nextPreset = getQrPresetConfig(draftValues.qrPreset || DEFAULT_QR_PRESET);

      setMode(draftValues.mode === 'pdf' ? 'pdf' : 'url');
      setBaseUrl(String(draftValues.baseUrl || 'https://roadhomeprogram.org/'));
      setUtmSource(String(draftValues.utmSource || ''));
      setUtmMedium(String(draftValues.utmMedium || ''));
      setUtmCampaign(String(draftValues.utmCampaign || ''));
      setPdfFile(null);
      setFileTitle(String(draftValues.fileTitle || ''));
      setForegroundColor(String(draftValues.foregroundColor || nextPreset.foregroundColor));
      setBackgroundColor(String(draftValues.backgroundColor || nextPreset.backgroundColor));
      setLogoFile(null);
      setLogoScale(clampQrLogoScale(draftValues.logoScale));
      setQrPreset(draftValues.qrPreset || DEFAULT_QR_PRESET);
      setDotStyle(draftValues.dotStyle || nextPreset.dotStyle);
      setCornerSquareStyle(draftValues.cornerSquareStyle || nextPreset.cornerSquareStyle);
      setCornerDotStyle(draftValues.cornerDotStyle || nextPreset.cornerDotStyle);
      setLinkType(draftValues.linkType || 'qr');
      setCustomShort(String(draftValues.customShort || ''));
      setUseCampaignAsShort(Boolean(draftValues.useCampaignAsShort));
      setSelectedOwnerId(String(draftValues.selectedOwnerId || userId || ''));
      setSavedResult(null);
      setHasUnsavedPayloadChanges(false);
      setDraftAutosaveEnabled(true);
      setDraftRestoreNotice('Draft restored.');
    };

    void restoreDraft();

    return () => {
      active = false;
    };
  }, [continueDraft, generatorDraftValues, restoreCandidate, userId]);

  useEffect(() => {
    if (user?.id && !selectedOwnerId) {
      setSelectedOwnerId(user.id);
    }
  }, [selectedOwnerId, user?.id]);

  const applyQrPreset = (presetKey) => {
    setDraftAutosaveEnabled(true);
    setDraftRestoreNotice('');
    const preset = getQrPresetConfig(presetKey);
    setQrPreset(presetKey);
    setForegroundColor(preset.foregroundColor);
    setBackgroundColor(preset.backgroundColor);
    setDotStyle(preset.dotStyle);
    setCornerSquareStyle(preset.cornerSquareStyle);
    setCornerDotStyle(preset.cornerDotStyle);
  };

  const markCustomPreset = () => {
    setDraftAutosaveEnabled(true);
    setDraftRestoreNotice('');
    setQrPreset('custom');
  };

  const updateDraftedState = (setter) => (value) => {
    setDraftAutosaveEnabled(true);
    setDraftRestoreNotice('');
    setter(value);
  };

  // Collision check helper: try up to 3 variants
  const resolveUniqueShortcode = async (candidate) => {
    const base = sanitizeShortcode(candidate);
    let code = base || generateShortCode(); // fallback if blank

    for (let i = 0; i < 3; i++) {
      const { data, error } = await supabase
        .from('qr_redirects')
        .select('id')
        .eq('short_code', code)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Supabase select error (short_code check):', error);
        break;
      }

      if (!data) return code; // not found → OK

      // found → add small suffix and try again
      code = withRandomSuffix(base || 'link', 3);
    }

    // If still colliding, final fallback to a random code
    return generateShortCode();
  };

  const getChosenShortcodeSeed = () => {
    if (customShort) return sanitizeShortcode(customShort);
    if (mode === 'url' && useCampaignAsShort && normalizedUtmCampaign) {
      return sanitizeShortcode(normalizedUtmCampaign);
    }
    return '';
  };

  const getSavedTitle = (code) => {
    const customShortValue = sanitizeShortcode(customShort);
    const campaignValue = utmCampaign.trim();
    const fileTitleValue = fileTitle.trim();

    return customShortValue || campaignValue || fileTitleValue || code;
  };

  const getDisplayName = () => {
    const customShortValue = sanitizeShortcode(customShort);
    const fileTitleValue = fileTitle.trim();

    return customShortValue || fileTitleValue || null;
  };

  const previewShortcode = (() => {
    if (customShort) return sanitizeShortcode(customShort) || '(auto-generated)';
    if (mode === 'url' && useCampaignAsShort && normalizedUtmCampaign) {
      return sanitizeShortcode(normalizedUtmCampaign) || '(auto-generated)';
    }
    return '(auto-generated)';
  })();

  const handleSaveUrl = async () => {
    // Guard: need a valid base URL to save
    if (!isBaseUrlValid) {
      console.error('Save blocked: baseUrl is empty or invalid:', baseUrl);
      setErrorMessage('Please enter a valid base URL (including https://) before saving.');
      setErrorSnackbar(true);
      return;
    }

    const appOrigin = getAppOrigin();
    if (!appOrigin) {
      setErrorMessage(APP_ORIGIN_REQUIRED_MESSAGE);
      setErrorSnackbar(true);
      return;
    }

    // 1) Decide shortcode source: custom → campaign (if opted) → random
    const chosen = getChosenShortcodeSeed();

    // 2) Ensure uniqueness
    const code = await resolveUniqueShortcode(chosen);
    const short = `${appOrigin}/${code}`;
    const qrPayloadUrl = short;
    const title = getSavedTitle(code);
    const displayName = getDisplayName();
    const attribution = buildStaffAttributionFields({
      currentUserId: user?.id,
      selectedOwnerId,
    });

    if (!attribution) {
      setErrorMessage(STAFF_AUTH_REQUIRED_MESSAGE);
      setErrorSnackbar(true);
      return;
    }

    const fullTargetUrl = buildTrackedUrl({
      baseUrl,
      utmSource: normalizedUtmSource,
      utmMedium: normalizedUtmMedium,
      utmCampaign: normalizedUtmCampaign,
    });
    let logoMetadata;

    try {
      logoMetadata = await resolveQrLogoMetadata({
        supabase,
        bucket: QR_LOGO_BUCKET,
        shortCode: code,
        logoFile,
        defaultLogoUrl: shieldLogo,
        logoScale: effectiveLogoScale,
      });
    } catch (logoError) {
      console.error('❌ Error uploading logo:', logoError);
      setErrorMessage(logoError.message || 'Logo upload failed. Please try again.');
      setErrorSnackbar(true);
      return;
    }

    const redirectEntry = {
      short_code: code,
      title,
      display_name: displayName,
      full_url: fullTargetUrl,
      target_type: 'url',
      ...attribution,
    };

    const historyEntry = {
      base_url: baseUrl,
      title,
      display_name: displayName,
      utm_source: normalizedUtmSource,
      utm_medium: normalizedUtmMedium,
      utm_campaign: normalizedUtmCampaign,
      utm_term: '',
      utm_content: '',
      qr_fg_color: foregroundColor,
      qr_bg_color: backgroundColor,
      logo_url: logoMetadata.logo_url,
      logo_scale: logoMetadata.logo_scale,
      qr_payload_url: qrPayloadUrl,
      logo_storage_path: logoMetadata.logo_storage_path,
      logo_mime_type: logoMetadata.logo_mime_type,
      logo_filename: logoMetadata.logo_filename,
      download_format: 'png',
      notes: '',
      full_url: fullTargetUrl,
      short_code: code,
      target_type: 'url',
      link_type: linkType,
      has_qr: linkType !== 'link',
      pdf_bucket: null,
      pdf_path: null,
      pdf_filename: null,
      file_title: null,
      mime_type: null,
      file_size: null,
      ...attribution,
    };

    const { error: redirectError } = await supabase.from('qr_redirects').insert([redirectEntry]);

    if (redirectError) {
      console.error('❌ Error saving redirect record:', redirectError);
      if (logoMetadata.uploaded) {
        await supabase.storage.from(QR_LOGO_BUCKET).remove([logoMetadata.logo_storage_path]).catch(() => {});
      }
      setErrorMessage(redirectError?.message || 'Error saving QR code. Please try again.');
      setErrorSnackbar(true);
      return;
    }

    const { error: historyError } = await supabase.from('qr_utm_generator_logs').insert([historyEntry]);

    if (historyError) {
      console.error('❌ Error saving generator history:', historyError);
      if (logoMetadata.uploaded) {
        await supabase.storage.from(QR_LOGO_BUCKET).remove([logoMetadata.logo_storage_path]).catch(() => {});
      }
      setErrorMessage(historyError?.message || 'Short link saved, but generator history failed to save.');
      setErrorSnackbar(true);
      return;
    }

    // 5) Success UI updates
    setSavedResult({ shortCode: code, shortUrl: short, formSignature, pdfFile, logoFile });
    setHasUnsavedPayloadChanges(false);
    await clearDraft({ status: 'submitted' });
    setOpenSnackbar(true);
    setErrorMessage('Error saving QR code. Please try again.');
  };

  const handleSavePdf = async () => {
    if (!fileTitle.trim()) {
      setErrorMessage('Please enter a document title before saving.');
      setErrorSnackbar(true);
      return;
    }

    if (!pdfFile) {
      setErrorMessage('Please upload a PDF before saving.');
      setErrorSnackbar(true);
      return;
    }

    if (pdfFile.type && pdfFile.type !== 'application/pdf') {
      setErrorMessage('The uploaded file must be a PDF.');
      setErrorSnackbar(true);
      return;
    }

    const appOrigin = getAppOrigin();
    if (!appOrigin) {
      setErrorMessage(APP_ORIGIN_REQUIRED_MESSAGE);
      setErrorSnackbar(true);
      return;
    }

    const chosen = getChosenShortcodeSeed();
    const code = await resolveUniqueShortcode(chosen);
    const short = `${appOrigin}/${code}`;
    const qrPayloadUrl = short;
    const title = getSavedTitle(code);
    const displayName = getDisplayName();
    const attribution = buildStaffAttributionFields({
      currentUserId: user?.id,
      selectedOwnerId,
    });
    const safeFilename = sanitizeFilename(pdfFile.name || `${fileTitle}.pdf`);
    const filePath = `${code}/${Date.now()}-${safeFilename}`;
    const storageReference = `storage://${PDF_BUCKET}/${filePath}`;
    let logoMetadata;

    if (!attribution) {
      setErrorMessage(STAFF_AUTH_REQUIRED_MESSAGE);
      setErrorSnackbar(true);
      return;
    }

    const { error: uploadError } = await supabase.storage
      .from(PDF_BUCKET)
      .upload(filePath, pdfFile, {
        contentType: pdfFile.type || 'application/pdf',
        upsert: false,
      });

    if (uploadError) {
      console.error('❌ Error uploading PDF:', uploadError);
      setErrorMessage(uploadError.message || 'PDF upload failed. Please try again.');
      setErrorSnackbar(true);
      return;
    }

    try {
      logoMetadata = await resolveQrLogoMetadata({
        supabase,
        bucket: QR_LOGO_BUCKET,
        shortCode: code,
        logoFile,
        defaultLogoUrl: shieldLogo,
        logoScale: effectiveLogoScale,
      });
    } catch (logoError) {
      console.error('❌ Error uploading logo:', logoError);
      await supabase.storage.from(PDF_BUCKET).remove([filePath]).catch(() => {});
      setErrorMessage(logoError.message || 'Logo upload failed. Please try again.');
      setErrorSnackbar(true);
      return;
    }

    const redirectEntry = {
      short_code: code,
      title,
      display_name: displayName,
      full_url: storageReference,
      target_type: 'pdf',
      pdf_bucket: PDF_BUCKET,
      pdf_path: filePath,
      pdf_filename: pdfFile.name,
      ...attribution,
    };

    const { error: redirectError } = await supabase.from('qr_redirects').insert([redirectEntry]);

    if (redirectError) {
      console.error('❌ Error saving PDF redirect record:', redirectError);
      await supabase.storage.from(PDF_BUCKET).remove([filePath]).catch((cleanupError) => {
        console.error('Failed to clean up uploaded PDF after redirect insert failure:', cleanupError);
      });
      if (logoMetadata.uploaded) {
        await supabase.storage.from(QR_LOGO_BUCKET).remove([logoMetadata.logo_storage_path]).catch(() => {});
      }
      setErrorMessage(redirectError.message || 'PDF uploaded, but redirect record failed to save.');
      setErrorSnackbar(true);
      return;
    }

    const historyEntry = {
      base_url: '',
      title,
      display_name: displayName,
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_term: '',
      utm_content: '',
      qr_fg_color: foregroundColor,
      qr_bg_color: backgroundColor,
      logo_url: logoMetadata.logo_url,
      logo_scale: logoMetadata.logo_scale,
      qr_payload_url: qrPayloadUrl,
      logo_storage_path: logoMetadata.logo_storage_path,
      logo_mime_type: logoMetadata.logo_mime_type,
      logo_filename: logoMetadata.logo_filename,
      download_format: 'png',
      notes: '',
      full_url: short,
      short_code: code,
      target_type: 'pdf',
      pdf_bucket: PDF_BUCKET,
      pdf_path: filePath,
      pdf_filename: pdfFile.name,
      file_title: fileTitle.trim(),
      mime_type: pdfFile.type || 'application/pdf',
      file_size: pdfFile.size || null,
      link_type: linkType,
      has_qr: linkType !== 'link',
      ...attribution,
    };

    const { error: historyError } = await supabase.from('qr_utm_generator_logs').insert([historyEntry]);

    if (historyError) {
      console.error('❌ Error saving PDF generator history:', historyError);
      await supabase.storage.from(PDF_BUCKET).remove([filePath]).catch((cleanupError) => {
        console.error('Failed to clean up uploaded PDF after history insert failure:', cleanupError);
      });
      if (logoMetadata.uploaded) {
        await supabase.storage.from(QR_LOGO_BUCKET).remove([logoMetadata.logo_storage_path]).catch(() => {});
      }
      setErrorMessage(historyError.message || 'PDF saved, but generator history failed to save.');
      setErrorSnackbar(true);
      return;
    }

    setSavedResult({ shortCode: code, shortUrl: short, formSignature, pdfFile, logoFile });
    setHasUnsavedPayloadChanges(false);
    await clearDraft({ status: 'submitted' });
    setOpenSnackbar(true);
    setErrorMessage('Error saving QR code. Please try again.');
  };

  const handleSaveQr = async () => {
    setIsSaving(true);
    try {
      if (mode === 'pdf') {
        await handleSavePdf();
        return;
      }

      await handleSaveUrl();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            QR Code Generator
          </Typography>
          <Typography color="text.secondary">
            Create branded QR codes, tracked short links, and PDF redirects with save-before-export protection.
          </Typography>
        </Box>

        {draftRestoreNotice ? (
          <Alert severity="info" variant="outlined">
            {draftRestoreNotice}
          </Alert>
        ) : null}

        {draftStatusTone === 'error' && draftStatusText ? (
          <Alert severity="warning" variant="outlined">
            {draftStatusText}. You can still save this QR manually.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gap: 3,
            alignItems: 'start',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(0, 1.35fr) minmax(360px, 0.95fr)',
            },
          }}
        >
          <Stack spacing={3}>
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h6">Link setup</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose a destination URL or PDF, then enter the tracking details staff use every day.
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={(_, nextMode) => {
                      if (nextMode) {
                        setDraftAutosaveEnabled(true);
                        setDraftRestoreNotice('');
                        setMode(nextMode);
                      }
                    }}
                    size="small"
                    color="primary"
                    disabled={isSaving}
                  >
                    <ToggleButton value="url">URL</ToggleButton>
                    <ToggleButton value="pdf">PDF</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                {mode === 'url' ? (
                  <>
                    <TargetUrlInput
                      baseUrl={baseUrl}
                      setBaseUrl={updateDraftedState(setBaseUrl)}
                      finalUrl={trackedDestinationUrl}
                      showBaseUrlError={showBaseUrlError}
                      disabled={isSaving}
                    />
                    <UtmFields
                      utmSource={utmSource}
                      setUtmSource={updateDraftedState(setUtmSource)}
                      utmMedium={utmMedium}
                      setUtmMedium={updateDraftedState(setUtmMedium)}
                      utmCampaign={utmCampaign}
                      setUtmCampaign={updateDraftedState(setUtmCampaign)}
                      disabled={isSaving}
                    />
                  </>
                ) : (
                  <PdfModeFields
                    fileTitle={fileTitle}
                    setFileTitle={updateDraftedState(setFileTitle)}
                    pdfFile={pdfFile}
                    setPdfFile={updateDraftedState(setPdfFile)}
                    disabled={isSaving}
                  />
                )}
              </Stack>
            </Paper>

            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Short link and ownership</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Set an optional shortcode, choose the output type, and assign the record owner.
                  </Typography>
                </Box>

                <Stack spacing={1}>
                  <TextField
                    label="Custom Shortcode (optional)"
                    placeholder="e.g., jonmurphy, weekly-recap-0805"
                    value={customShort}
                    onChange={(e) => updateDraftedState(setCustomShort)(e.target.value)}
                    fullWidth
                    disabled={isSaving}
                    inputProps={{ maxLength: 64 }}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={useCampaignAsShort}
                        onChange={(e) => updateDraftedState(setUseCampaignAsShort)(e.target.checked)}
                        disabled={isSaving}
                      />
                    )}
                    label="Use campaign as shortcode if custom is blank"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Shortcode preview: {previewShortcode || '(auto-generated)'}
                  </Typography>
                </Stack>

                <SaveOptions linkType={linkType} setLinkType={updateDraftedState(setLinkType)} disabled={isSaving} />
                <StaffOwnerSelect
                  value={selectedOwnerId}
                  onChange={updateDraftedState(setSelectedOwnerId)}
                  currentUser={user}
                  currentProfile={profile}
                  label="Owner"
                  helperText="New QR and short-link records keep you as creator and assign this owner."
                  disabled={isSaving}
                />
              </Stack>
            </Paper>

            {linkType !== 'link' && (
              <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6">QR appearance</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Adjust colors, style presets, and logo placement without changing the saved destination.
                    </Typography>
                  </Box>

                  <ColorPicker
                    foregroundColor={foregroundColor}
                    setForegroundColor={(value) => {
                      markCustomPreset();
                      setForegroundColor(value);
                    }}
                    backgroundColor={backgroundColor}
                    setBackgroundColor={(value) => {
                      markCustomPreset();
                      setBackgroundColor(value);
                    }}
                    disabled={isSaving}
                  />
                  <QrStyleControls
                    preset={qrPreset}
                    dotStyle={dotStyle}
                    cornerSquareStyle={cornerSquareStyle}
                    cornerDotStyle={cornerDotStyle}
                    onPresetChange={(presetKey) => {
                      if (presetKey === 'custom') {
                        setDraftAutosaveEnabled(true);
                        setDraftRestoreNotice('');
                        setQrPreset('custom');
                        return;
                      }
                      applyQrPreset(presetKey);
                    }}
                    onDotStyleChange={(value) => {
                      markCustomPreset();
                      setDotStyle(value);
                    }}
                    onCornerSquareStyleChange={(value) => {
                      markCustomPreset();
                      setCornerSquareStyle(value);
                    }}
                    onCornerDotStyleChange={(value) => {
                      markCustomPreset();
                      setCornerDotStyle(value);
                    }}
                    showContrastWarning={showContrastWarning}
                    foregroundColor={foregroundColor}
                    backgroundColor={backgroundColor}
                    disabled={isSaving}
                  />
                  <LogoUpload
                    logoFile={logoFile}
                    setLogoFile={setLogoFile}
                    logoScale={effectiveLogoScale}
                    setLogoScale={updateDraftedState(setLogoScale)}
                    disabled={isSaving}
                  />
                </Stack>
              </Paper>
            )}
          </Stack>

          <Stack spacing={3} sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">{isLinkOnly ? 'Link workflow' : 'QR workflow'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isLinkOnly
                      ? 'Generate first to save the tracked short link in Supabase, then copy it from this panel.'
                      : 'Generate first to save the short link in Supabase, then copy or download the QR.'}
                  </Typography>
                </Box>

                {linkType !== 'link' ? (
                  <QrCanvas
                    ref={canvasRef}
                    targetUrl={previewTarget}
                    foregroundColor={foregroundColor}
                    backgroundColor={backgroundColor}
                    logoFileOrUrl={logoFile || shieldLogo}
                    logoScale={effectiveLogoScale}
                    dotStyle={dotStyle}
                    cornerSquareStyle={cornerSquareStyle}
                    cornerDotStyle={cornerDotStyle}
                    preset={qrPreset === 'custom' ? DEFAULT_QR_PRESET : qrPreset}
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: 220,
                      borderRadius: 3,
                      border: '1px dashed',
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                      display: 'grid',
                      placeItems: 'center',
                      px: 3,
                      textAlign: 'center',
                    }}
                  >
                    {linkType === 'link' ? (
                      <Typography variant="body2" color="text.secondary">
                        Link-only output is selected. Generate the tracked short link here, or switch to QR Only or Both to unlock QR preview and downloads after save.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Generate QR to create a tracked Road Home short link before copying or downloading.
                      </Typography>
                    )}
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {isLinkOnly ? 'Tracked short link status' : 'QR status'}
                  </Typography>
                  {isLinkOnly ? (
                    <Typography variant="body2" color="text.secondary">
                      {hasSavedQr
                        ? 'Tracked short link is ready to copy.'
                        : 'Generate Link to create a tracked Road Home short link before copying it.'}
                    </Typography>
                  ) : (
                    <Alert severity="info" variant="outlined">
                      {previewStatusCopy}
                    </Alert>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    Short link result
                  </Typography>
                  {shortUrl ? (
                    <>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 700, mb: 0.75 }}>
                        {isLinkOnly ? '✓ Link Saved and tracking active' : '✓ QR Saved and tracking active'}
                      </Typography>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        <a href={shortUrl} target="_blank" rel="noopener noreferrer">
                          {shortUrl}
                        </a>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Short code: {shortCode}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {isLinkOnly
                        ? `Generate Link to save the tracked short link first. Current shortcode preview: ${previewShortcode}.`
                        : `Generate QR to save the tracked short link first. Current shortcode preview: ${previewShortcode}.`}
                    </Typography>
                  )}
                </Box>

                <ExportButtons
                  targetUrl={shortUrl}
                  shortUrl={shortUrl}
                  canvasRef={canvasRef}
                  onSave={handleSaveQr}
                  linkType={linkType}
                  disableSave={disableSave}
                  isSaved={hasSavedQr}
                  foregroundColor={foregroundColor}
                  backgroundColor={backgroundColor}
                  logoFileOrUrl={logoFile || shieldLogo}
                  logoScale={effectiveLogoScale}
                  dotStyle={dotStyle}
                  cornerSquareStyle={cornerSquareStyle}
                  cornerDotStyle={cornerDotStyle}
                  preset={qrPreset === 'custom' ? DEFAULT_QR_PRESET : qrPreset}
                  shortCode={shortCode}
                  utmCampaign={utmCampaign}
                  utmSource={utmSource}
                  isSaving={isSaving}
                />
              </Stack>
            </Paper>
          </Stack>
        </Box>

      <ToastAlerts
        openSnackbar={openSnackbar}
        setOpenSnackbar={setOpenSnackbar}
        errorSnackbar={errorSnackbar}
        setErrorSnackbar={setErrorSnackbar}
        shortUrl={shortUrl}
        errorMessage={errorMessage}
      />
      </Stack>
    </Container>
  );
};

export default GeneratorPage;
